import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Loader2, Send, Sparkles, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { orgToday } from "@/lib/tz";
import { EXPORT_STATUSES, notesForExport } from "@/lib/shiftNotes";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { RichTextEditor, isEmptyHtml } from "@/components/RichTextEditor";

const CRIMSON = "var(--brand)";
const STATUS_HINT = { Approved: "timesheet approved", Submitted: "not yet approved", Returned: "sent back to the worker" };

// A blob request's error body arrives as a blob too.
async function errorText(e, fallback) {
  try {
    const d = e.response?.data;
    return (d instanceof Blob ? JSON.parse(await d.text()) : d)?.detail || fallback;
  } catch { return fallback; }
}

// Download a participant's shift notes as a PDF: pick the dates, which notes
// (Approved by default; drafts are never exported) and whether to include
// attachments, and optionally add an AI summary the user edits, by hand or by
// chatting to the AI, before the PDF is made.
export function ShiftNotesExportDialog({ open, onOpenChange, participant, rows, range }) {
  const { can } = useAuth();
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [statuses, setStatuses] = useState(["Approved"]);
  const [attachments, setAttachments] = useState(false);
  const [withSummary, setWithSummary] = useState(false);
  const [summary, setSummary] = useState("");
  const [summaryScope, setSummaryScope] = useState(null);   // what the summary was written for
  const [writing, setWriting] = useState(false);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const chatEnd = useRef(null);

  // Each time it opens, start from the tab's date range (an open end becomes
  // the first note / today, because the export needs both dates).
  useEffect(() => {
    if (!open) return;
    const dates = rows.map((r) => String(r.shift_date || "").slice(0, 10)).filter(Boolean).sort();
    setFrom(range.from || dates[0] || orgToday());
    setTo(range.to || orgToday());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  useEffect(() => { chatEnd.current?.scrollIntoView({ block: "end" }); }, [messages, asking]);

  const picked = notesForExport(rows, from, to, statuses);
  const scope = { date_from: from, date_to: to, statuses };
  const scopeKey = JSON.stringify([from, to, statuses]);
  const stale = withSummary && summaryScope && summaryScope !== scopeKey;
  const files = picked.reduce((n, r) => n + (r.attachments || []).length, 0);
  const datesOk = from && to && from <= to;

  const toggleStatus = (s, on) => setStatuses((cur) => EXPORT_STATUSES.filter((x) => (x === s ? on : cur.includes(x))));

  const write = async () => {
    setWriting(true);
    try {
      const r = await api.shiftNotesExportSummary(participant, scope);
      setSummary(r.summary_html || "");
      setSummaryScope(scopeKey);
      setMessages([]);
      if (r.sent < r.count) toast.warning(`There were too many notes for the AI to read at once: it summarised the first ${r.sent} of ${r.count}.`);
    } catch (e) { toast.error(await errorText(e, "Could not write the summary")); }
    finally { setWriting(false); }
  };

  const ask = async () => {
    const text = draft.trim();
    if (!text || asking) return;
    const next = [...messages, { role: "user", content: text }];
    setMessages(next); setDraft(""); setAsking(true);
    try {
      const r = await api.shiftNotesExportChat(participant, { ...scope, summary_html: summary, messages: next });
      setMessages([...next, { role: "assistant", content: r.reply }]);
      if (r.summary_html != null) setSummary(r.summary_html);
    } catch (e) {
      setMessages([...next, { role: "assistant", content: await errorText(e, "The AI couldn't answer. Try again."), error: true }]);
    } finally { setAsking(false); }
  };

  const download = async () => {
    setBusy(true);
    try {
      const blob = await api.shiftNotesExportPdf(participant, { ...scope, include_attachments: attachments,
        summary_html: withSummary && !isEmptyHtml(summary) ? summary : null });
      const u = URL.createObjectURL(blob); const a = document.createElement("a");
      a.href = u; a.download = `ShiftNotes-${participant}-${from}_to_${to}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(u);
      onOpenChange(false);
    } catch (e) { toast.error(await errorText(e, "Could not make the PDF")); }
    finally { setBusy(false); }
  };

  const needsSummary = withSummary && isEmptyHtml(summary);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={withSummary && summaryScope ? "max-w-6xl" : "max-w-xl"} data-testid="shift-notes-export-dialog">
        <DialogHeader><DialogTitle>Download shift notes</DialogTitle></DialogHeader>
        <div className={withSummary && summaryScope ? "grid gap-5 lg:grid-cols-[300px_1fr]" : ""}>
          <div className="space-y-4">
            <div className="flex flex-wrap gap-3">
              <div><Label className="text-xs text-slate-500">From</Label>
                <Input type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} className="h-9 text-sm w-40" data-testid="export-from" /></div>
              <div><Label className="text-xs text-slate-500">To</Label>
                <Input type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} className="h-9 text-sm w-40" data-testid="export-to" /></div>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Notes to include</Label>
              <div className="mt-1.5 space-y-1.5">
                {EXPORT_STATUSES.map((s) => (
                  <label key={s} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                    <Checkbox checked={statuses.includes(s)} onCheckedChange={(v) => toggleStatus(s, !!v)} data-testid={`export-status-${s.toLowerCase()}`} />
                    {s} <span className="text-xs text-slate-400">({STATUS_HINT[s]})</span>
                  </label>
                ))}
              </div>
              <p className="text-[11px] text-slate-400 mt-1">Draft notes can't be exported: the worker hasn't finished them.</p>
            </div>
            <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
              <Checkbox className="mt-0.5" checked={attachments} onCheckedChange={(v) => setAttachments(!!v)} data-testid="export-attachments" />
              <span>Include attachments{files ? ` (${files})` : ""}
                <span className="block text-[11px] text-slate-400">Photos and PDF pages are shrunk to keep the file small. Other files are listed by name.</span></span>
            </label>
            <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
              <Checkbox className="mt-0.5" checked={withSummary} onCheckedChange={(v) => setWithSummary(!!v)} data-testid="export-ai-summary" />
              <span>Add an AI summary at the top
                <span className="block text-[11px] text-slate-400">
                  You can edit it, or ask the AI to change it, before downloading.
                  {can.has("settings.ai") && <Link to="/settings/ai/shift_notes" className="ml-1 underline hover:text-slate-600">AI settings</Link>}
                </span></span>
            </label>
            {withSummary && (
              <Button size="sm" variant="outline" onClick={write} disabled={writing || picked.length === 0 || !datesOk} data-testid="export-write-summary">
                {writing ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : summaryScope ? <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
                {writing ? "Writing…" : summaryScope ? "Write it again" : "Write summary"}
              </Button>
            )}
            {stale && <p className="text-xs text-amber-700" data-testid="export-summary-stale">The dates or notes have changed since the summary was written. Write it again, or check it still fits.</p>}
            <p className="text-xs text-slate-500" data-testid="export-count">{picked.length} note{picked.length === 1 ? "" : "s"} will be included.</p>
          </div>

          {withSummary && summaryScope && (
            <div className="grid gap-4 md:grid-cols-[1fr_280px] min-w-0">
              <div className="min-w-0">
                <Label className="text-xs text-slate-500">Summary</Label>
                <div className="mt-1 max-h-[55vh] overflow-y-auto">
                  <RichTextEditor value={summary} onChange={setSummary} headings links placeholder="The summary that goes at the top of the PDF…" testid="export-summary-editor" />
                </div>
              </div>
              <div className="flex flex-col rounded-lg border border-slate-200 bg-slate-50 min-h-[260px] max-h-[60vh]" data-testid="export-chat">
                <div className="px-3 py-2 border-b border-slate-200 text-xs font-medium text-slate-600 flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5" style={{ color: CRIMSON }} />Ask the AI to change it</div>
                <div className="flex-1 overflow-y-auto p-2.5 space-y-2 text-sm">
                  {messages.length === 0 && <p className="text-xs text-slate-400">For example: "make it shorter", "add more about her mood", "list the dates she went swimming".</p>}
                  {messages.map((m, i) => (
                    <div key={i} className={`rounded-md px-2.5 py-1.5 whitespace-pre-wrap ${m.role === "user" ? "bg-white border border-slate-200 ml-6" : m.error ? "bg-brand-soft text-rose-700 mr-6" : "bg-brand-soft text-slate-700 mr-6"}`}>{m.content}</div>
                  ))}
                  {asking && <div className="flex items-center gap-1.5 text-xs text-slate-500"><Loader2 className="h-3.5 w-3.5 animate-spin" />Working on it…</div>}
                  <div ref={chatEnd} />
                </div>
                <div className="p-2 border-t border-slate-200 flex gap-1.5">
                  <Textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={2} placeholder="Type a change…" className="text-sm min-h-0 resize-none bg-white"
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(); } }} data-testid="export-chat-input" />
                  <Button size="icon" className="h-auto w-9 shrink-0" style={{ backgroundColor: CRIMSON }} onClick={ask} disabled={asking || !draft.trim()} data-testid="export-chat-send"><Send className="h-4 w-4" /></Button>
                </div>
              </div>
            </div>
          )}
        </div>
        <DialogFooter className="items-center gap-2">
          {needsSummary && <span className="text-xs text-slate-500 mr-auto">Write the summary first, or untick it.</span>}
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={download} disabled={busy || writing || asking || picked.length === 0 || !datesOk || needsSummary}
            style={{ backgroundColor: CRIMSON }} data-testid="export-download">
            {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />}Download PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
